import { Classes, Switch } from "@blueprintjs/core";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanrCheckbox } from "modules/common/components/planr/checkbox/PlanrCheckbox";
import { Search } from "modules/common/components/form/Search";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { apiUrls } from "modules/common/services/communication/urls";
import { DefaultAgGridOptions } from "modules/common/services/table/helpers";
import { useMst } from "modules/root/models/hooks";
import { agGridLocale } from "modules/root/services/locale";
import { AccessBlockInfo } from "modules/session/access-links/access-link-actions";
import { BlockAccessDialog } from "modules/session/access-links/BlockAccessDialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CredentialsDialog, Credentials } from "./CredentialsDialog";
import styles from "./UsersAccess.module.scss";

interface UserAccessRow {
    target: "employer" | "outsourcer";
    id: string;
    name: string;
    kind: string;
    login: string;
    place: string;
    fired: boolean;
    hasPassword: boolean;
    hasEmail: boolean;
    godMode: boolean;
    isSelf: boolean;
    blocked: AccessBlockInfo | null;
}

/** Окно пароля: credentials null - ещё спрашиваем подтверждение */
type PasswordState = { row: UserAccessRow; credentials: Credentials | null } | null;

const KINDS = [
    { id: "", label: "Все" },
    { id: "Сотрудник", label: "Сотрудники" },
    { id: "Подрядчик", label: "Подрядчики" },
    { id: "Агент", label: "Агенты" },
    { id: "Заказчик", label: "Заказчики" },
];

interface Actions {
    block: (row: UserAccessRow) => void;
    unblock: (row: UserAccessRow) => void;
    generate: (row: UserAccessRow) => void;
    sendLink: (row: UserAccessRow) => void;
}

/** Новый пароль: заказчики, контрагенты и сотрудники - каждый своим методом, как в карточках */
const credentialsUrl = (row: UserAccessRow) =>
    row.target === "employer"
        ? apiUrls.employee.credentials(row.id)
        : row.kind === "Заказчик"
          ? apiUrls.clients.credentials(row.id)
          : apiUrls.outsourcers.credentials(row.id);

const linkUrl = (row: UserAccessRow) =>
    row.target === "employer" ? apiUrls.accessLinks.employeeLink(row.id) : apiUrls.accessLinks.outsourcerLink(row.id);

/**
 * Все, у кого есть учётная запись, и выключатель доступа к приложению.
 * Отключённый сразу выходит на всех устройствах и не может войти, пока доступ не включат.
 * Список грузится при первом открытии вкладки: она живёт внутри страницы настроек.
 */
export const UsersAccess = ({ active }: { active: boolean }) => {
    const root = useMst();
    const [rows, setRows] = useState<UserAccessRow[] | null>(null);
    const [search, setSearch] = useState("");
    const [kind, setKind] = useState("");
    const [onlyBlocked, setOnlyBlocked] = useState(false);
    const [showFired, setShowFired] = useState(false);
    const [blocking, setBlocking] = useState<UserAccessRow | null>(null);
    const [password, setPassword] = useState<PasswordState>(null);

    const load = useCallback(async () => {
        try {
            const data: UserAccessRow[] = await root.transport.get<UserAccessRow[]>(apiUrls.userAccess.list);
            setRows(data);
        } catch (er) {
            root.notify.error(er);
        }
    }, [root]);

    useEffect(() => {
        active && rows === null && load();
    }, [active, rows, load]);

    const replace = (row: UserAccessRow) =>
        setRows((current) => current && current.map((r) => (r.target === row.target && r.id === row.id ? row : r)));

    const setAccess = async (row: UserAccessRow, enabled: boolean, reason = "") => {
        try {
            const updated: UserAccessRow = await root.transport.post<UserAccessRow>(
                enabled ? apiUrls.userAccess.unblock(row.target, row.id) : apiUrls.userAccess.block(row.target, row.id),
                enabled ? undefined : { reason }
            );
            replace(updated);
            root.notify.success(enabled ? `Доступ включён: ${row.name}` : `Доступ отключён: ${row.name}`);
        } catch (er) {
            root.notify.error(er);
        }
    };

    /** Новый пароль сразу заменяет прежний - как кнопка «Сгенерировать пароль» в карточке */
    const generate = async (row: UserAccessRow) => {
        try {
            const credentials: Credentials = await root.transport.post<Credentials>(credentialsUrl(row), {});
            replace({ ...row, hasPassword: true });
            setPassword({ row, credentials });
            root.notify.success(
                row.hasPassword ? "Пароль изменён — старый больше не работает" : "Пароль сгенерирован"
            );
        } catch (er) {
            setPassword(null);
            root.notify.error(er);
        }
    };

    /** Ссылка на почту: смена пароля или приглашение, если пароля ещё нет */
    const sendLink = async (row: UserAccessRow) => {
        try {
            await root.transport.post(linkUrl(row), {});
            root.notify.success(row.hasPassword ? "Ссылка для смены пароля отправлена" : "Приглашение отправлено");
        } catch (er) {
            root.notify.error(er);
        }
    };

    /** Данные для входа на почту: сервер сверит пароль с текущим */
    const mailCredentials = async (row: UserAccessRow, value: string) => {
        try {
            const url =
                row.target === "employer"
                    ? apiUrls.accessLinks.employeeCredentialsMail(row.id)
                    : apiUrls.accessLinks.outsourcerCredentialsMail(row.id);
            await root.transport.post(url, { password: value });
            root.notify.success("Данные для входа отправлены на почту");
        } catch (er) {
            root.notify.error(er);
        }
    };

    // ячейки AG Grid берут действия из контекста: ссылка на него постоянная
    const actions = useRef<Actions>({ block: () => {}, unblock: () => {}, generate: () => {}, sendLink: () => {} });
    actions.current.block = (row) => setBlocking(row);
    actions.current.unblock = (row) => setAccess(row, true);
    // действующий пароль заменяется сразу - сначала спросим
    actions.current.generate = (row) => (row.hasPassword ? setPassword({ row, credentials: null }) : generate(row));
    actions.current.sendLink = sendLink;
    const context = useMemo(() => ({ actions }), []);

    const visible = useMemo(() => {
        const text = search.trim().toLowerCase();

        return (rows ?? []).filter(
            (r) =>
                (showFired || !r.fired) &&
                (!kind || r.kind === kind) &&
                (!onlyBlocked || r.blocked) &&
                (!text || `${r.name} ${r.login} ${r.place}`.toLowerCase().includes(text))
        );
    }, [rows, search, kind, onlyBlocked, showFired]);

    const blockedCount = (rows ?? []).filter((r) => r.blocked).length;
    const total = (rows ?? []).filter((r) => !r.fired).length;

    const kindOption = KINDS.find((k) => k.id === kind) ?? KINDS[0];

    return (
        <div className={styles.page}>
            <div className={styles.toolbar}>
                <div className={styles.kind}>
                    <SimpleSelect
                        className={`${Classes.FILL} planr-default-input`}
                        filterable={false}
                        items={KINDS}
                        itemRenderer={renderSingleOption}
                        onItemSelect={(item) => setKind(item.id)}
                    >
                        <DefaultSelectedOption option={kindOption} />
                    </SimpleSelect>
                </div>

                <PlanrCheckbox checked={onlyBlocked} onChange={setOnlyBlocked}>
                    Только отключённые
                </PlanrCheckbox>
                <PlanrCheckbox checked={showFired} onChange={setShowFired}>
                    Показать уволенных
                </PlanrCheckbox>

                <div className={styles.right}>
                    <span className={styles.counter}>
                        Отключено: {blockedCount} из {total}
                    </span>
                    <PlanrButton
                        type="graybtn"
                        size="small"
                        icon="general-refresh"
                        onClick={load}
                        title="Обновить"
                        round
                    />
                    <Search query={search} onSearch={setSearch} placeholder="ФИО, логин, отдел" style={{ marginLeft: 0 }} />
                </div>
            </div>

            <GridTheme className={`designed ${styles.table}`} bordered={true}>
                <AgGridReact
                    columnDefs={columns}
                    rowData={visible}
                    getRowId={({ data }) => `${data.target}:${data.id}`}
                    context={context}
                    localeText={agGridLocale}
                    suppressCellFocus={true}
                    enableCellTextSelection={true}
                    gridOptions={gridOptions}
                    overlayNoRowsTemplate={rows === null ? "Загрузка…" : "Никого не нашлось"}
                />
            </GridTheme>

            {password && (
                <CredentialsDialog
                    who={displayName(password.row)}
                    credentials={password.credentials}
                    onConfirm={() => generate(password.row)}
                    onCopied={() => root.notify.success("Данные для входа скопированы")}
                    onMail={() => mailCredentials(password.row, password.credentials?.password ?? "")}
                    mailDisabled={password.row.hasEmail ? undefined : "Почта не указана"}
                    onClose={() => setPassword(null)}
                />
            )}

            {blocking && (
                <BlockAccessDialog
                    who={displayName(blocking)}
                    onConfirm={(reason) => setAccess(blocking, false, reason)}
                    onClose={() => setBlocking(null)}
                />
            )}
        </div>
    );
};

const gridOptions = DefaultAgGridOptions();

/** «Петров Пётр (petrov)»; если логин уже в названии - без повтора */
const displayName = (row: UserAccessRow) => (row.name.includes(row.login) ? row.name : `${row.name} (${row.login})`);

/** «13.10.2026 14:05, Селезнёв К. — причина» */
const blockedText = (row?: UserAccessRow) =>
    row?.blocked
        ? `${row.blocked.at}${row.blocked.by ? `, ${row.blocked.by}` : ""}${
              row.blocked.reason ? ` — ${row.blocked.reason}` : ""
          }`
        : "";

/** Выключатель как на странице подтверждения устройств: выключить - с подтверждением */
const AccessCell = ({ data, context }: ICellRendererParams<UserAccessRow>) => {
    if (!data || data.fired) {
        return data?.fired ? <span className={styles.muted}>уволен</span> : null;
    }

    const actions = (context as { actions: React.RefObject<Actions> }).actions.current!;

    return (
        // кто, когда и почему отключил - подсказкой, отдельная колонка для этого не нужна.
        // Подсказка на обёртке: свой title Switch отдаёт скрытому полю, на нём её не видно
        <span
            className={styles.switchWrap}
            title={
                data.isSelf
                    ? "Свой доступ отключить нельзя"
                    : data.blocked
                      ? `Отключён ${blockedText(data)}`
                      : "Доступ есть"
            }
        >
            <Switch
                className={styles.switch}
                checked={!data.blocked}
                disabled={data.isSelf}
                onChange={() => (data.blocked ? actions.unblock(data) : actions.block(data))}
            />
        </span>
    );
};

/**
 * Пароль: сгенерировать новый или отправить ссылку на почту - то же, что блок «Доступ»
 * в карточке, но иконками: в таблице кнопки с подписями не помещаются
 */
const PasswordCell = ({ data, context }: ICellRendererParams<UserAccessRow>) => {
    // отключённому и уволенному пароль не нужен: войти они всё равно не смогут
    if (!data || data.fired || data.blocked || !data.login) {
        return null;
    }

    const actions = (context as { actions: React.RefObject<Actions> }).actions.current!;

    return (
        <div className={styles.passwordCell}>
            <PlanrButton
                type="neutral"
                size="small"
                round={true}
                icon="general-shuffle"
                title={data.hasPassword ? "Сгенерировать новый пароль" : "Сгенерировать пароль"}
                onClick={() => actions.generate(data)}
            />
            <PlanrButton
                type="neutral"
                size="small"
                round={true}
                icon="general-mail"
                disabled={!data.hasEmail}
                title={
                    !data.hasEmail
                        ? "Почта не указана — ссылку некуда отправить"
                        : data.hasPassword
                          ? "Отправить ссылку для смены пароля"
                          : "Отправить приглашение"
                }
                onClick={() => actions.sendLink(data)}
            />
        </div>
    );
};

const columns: ColDef<UserAccessRow>[] = [
    { headerName: "ФИО / организация", field: "name", flex: 1, minWidth: 220, sortable: true, resizable: true },
    { headerName: "Тип", field: "kind", width: 110, sortable: true, resizable: true },
    { headerName: "Логин", field: "login", width: 140, sortable: true, resizable: true },
    { headerName: "Отдел, должность", field: "place", flex: 1, minWidth: 200, sortable: true, resizable: true },
    { headerName: "Пароль", colId: "password", width: 100, cellRenderer: PasswordCell },
    {
        headerName: "Доступ",
        colId: "access",
        width: 90,
        sortable: true,
        valueGetter: ({ data }) => (data?.blocked ? 0 : 1),
        cellClass: "centered",
        cellRenderer: AccessCell,
    },
];
